import { ActionTypes as types } from '../constants/actionTypes';
import { i18n } from '../i18n/translate';
import { WindowHelperWrapper } from '../utils/WindowHelper';

// `e` stays `any`: legacy call sites pass fetch errors, Error instances, and
// redux-json-api `{ response }` shapes interchangeably (library-boundary any).
export const displayError = (e: any, windowOverride?: any) => {
  const statusCode = e.response && e.response.status;
  if (statusCode === 422 || statusCode === 500) {
    return async function (dispatch: any) {
      let err;
      try {
        err = await e.response.json();
      } catch (_error) {
        // must not be valid JSON, use a default error message
        err = { error: getDefaultErrorMessage(statusCode) };
      }
      if (err.errors) {
        if (typeof err.errors === 'string') {
          dispatch({
            type: types.DISPLAY_ERROR,
            message: err.errors,
          });
        } else if (err.errors[0].source) {
          // dispatch Homeroom errors
          const homeroomErrors = formatHomeroomFormErrors(err);
          dispatch(displayErrorMessages(homeroomErrors));
        } else {
          dispatch({
            type: types.DISPLAY_ERROR,
            message: err.errors
              .map((error: any) => {
                return error.title;
              })
              .join(', '),
          });
          dispatch({
            type: types.DISPLAY_ERRORS,
            errors: err.errors.map((error: any) => {
              return { error: error.title };
            }),
          });
        }
      } else if (err.error) {
        dispatch({
          type: types.DISPLAY_ERROR,
          message: err.error,
        });
      } else {
        dispatch({
          type: types.DISPLAY_ERRORS,
          errors: err,
        });
      }
    };
  } else if (statusCode === 401) {
    const windowHelper = WindowHelperWrapper(windowOverride);
    windowHelper.reload();
    return displayErrorMessage(i18n.t('Unauthorized'));
  } else {
    const errorMessage = e.message || getDefaultErrorMessage(statusCode);
    return displayErrorMessage(errorMessage);
  }
};

// `statusCode` can be any status number or undefined (no response on the error)
const getDefaultErrorMessage = (statusCode?: number) => {
  let errorMessage;
  switch (true) {
    // the non-null assertions preserve the legacy undefined-comparison semantics
    // (undefined fails every case and falls through to the default message)
    case statusCode === 403:
      errorMessage = i18n.t('The request was forbidden');
      break;
    case statusCode === 418:
      errorMessage = i18n.t('The server refuses the attempt to brew coffee with a teapot.');
      break;
    case statusCode! >= 400 && statusCode! < 500:
      errorMessage = i18n.t('The request was invalid');
      break;
    case statusCode! >= 500 && statusCode! < 600:
      errorMessage = i18n.t('Internal server error');
      break;
    default:
      errorMessage = i18n.t('Something went wrong');
  }
  return errorMessage;
};

// `message` stays `any`: callers pass strings, i18n.t results, and ReactNode messages.
export const displayErrorMessage = (message: any) => {
  return (dispatch: any) => {
    dispatch(clearMessages());
    dispatch({
      type: types.DISPLAY_ERROR,
      message,
    });
  };
};

// `errors` stays `any`: legacy callers pass arrays, keyed objects, and raw API error payloads.
export const displayErrorMessages = (errors: any) => {
  return (dispatch: any) => {
    dispatch(clearMessages());
    dispatch({
      type: types.DISPLAY_ERRORS,
      errors,
    });
  };
};

// `message` stays `any`: callers pass strings, i18n.t results, and ReactNode messages.
export const displaySuccessMessage = (message: any) => {
  return (dispatch: any) => {
    dispatch(clearMessages());
    dispatch({
      type: types.DISPLAY_SUCCESS,
      message,
    });
  };
};

// `errorMessages` stays `any`: raw JSON:API error payload (entity system not modeled).
export const formatHomeroomFormErrors = (errorMessages: any) => {
  return errorMessages.errors.reduce((acc: any, curr: any) => {
    const fieldName = curr.source.pointer.split('/').slice(3, 4)[0];
    if (fieldName) {
      acc[fieldName] = curr.detail;
    }
    return acc;
  }, {});
};

export const capitalizeMessage = (message: string) => {
  if (message.substring(0, 3) === 'sso') {
    return 'SSO' + message.slice(4);
  } else {
    return message.charAt(0).toUpperCase() + message.slice(1);
  }
};

export const clearMessages = () => {
  return {
    type: types.CLEAR_MESSAGES,
  };
};
